import { nextLetter, getImageCells, getDatesBetween } from "_utils/functions";
import { Logos } from "_utils/makeExcel-constants";

const firstColumnLabels = ["Staff", "PRM (ARR +DEP)", "Boarding Tasks", "Staff Breaks", "Shortfall"];  //removed "Depart STD" from list
const hoursColumns = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

const staffColor = "FFEBD6FB";
const zeroshortfall = "FFFDFFB8";
const goodShortfall = "FF4DFFBE";
const mediumShortfall = "FFFFB4B4";
const highShortfall = "FFE14434";
const departureColor = "FF307FE2";
const arrivalColor = "FF2be03d";


const normalizeKeys = (obj) => {
    const newObj = {};
    for (const key in obj) {
        newObj[key.trim()] = obj[key];
    }
    return newObj;
};
const sources = ["staffData", "prmBookings", "boardingDocs", "staffBreak", "shortfall"]; //removed "departureDocs", 
const getCellColor = (source, value) => {
    if (source === "staffData") return staffColor;
    if (source === "flights" && value === "Arrival") return arrivalColor
    if (source === "flights" && value === "Departure") return departureColor

    if (source === "shortfall") {
        if (value >= 10) return highShortfall;
        if (value > 3 && value < 10) return mediumShortfall;
        if (value >= 0 && value <= 3) return zeroshortfall;
        if (value < 0) return goodShortfall;
    }
    return undefined;
};

const renderShortfallMapSheet = ({
    sheet,
    data,
    daysList,
}) => {

    daysList.forEach((dayLabel) => {
        const row = sheet.addRow([]);
        const dateObj = new Date(dayLabel);

        // Column A = Day name (e.g., Mon)
        row.getCell("A").value = dateObj.toLocaleDateString("en-GB", {
            weekday: "short",
        });
        row.getCell("A").alignment = { vertical: "middle", horizontal: "left" };

        // Column B = Full date (e.g., 15 Jul)
        row.getCell("B").value = dateObj.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
        });
        row.getCell("B").alignment = { vertical: "middle", horizontal: "left" };

        // Get values for this day
        const hourlyValues = hoursColumns.map((hour) => {
            return data?.[dayLabel]?.[hour] ?? 0;
        });


        // Fill Columns C to X
        hourlyValues.forEach((val, idx) => {
            const colLetter = String.fromCharCode("C".charCodeAt(0) + idx);
            const cell = row.getCell(colLetter);
            cell.value = val;

            cell.alignment = { vertical: "middle", horizontal: "center" };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };

            const color = getCellColor("shortfall", val);
            if (color) {
                cell.fill = {
                    type: "pattern",
                    pattern: "solid",
                    fgColor: { argb: color },
                };
            }
        });
    });
};

const renderFlightsSheet = ({ sheet, data, daysList }) => {
    const getFormattedDate = (dateStr) => {
        const date = new Date(dateStr);
        return {
            weekday: date.toLocaleDateString("en-GB", { weekday: "short" }),
            fullDate: date.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
        };
    };

    const styleCell = (cell) => {
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
        };
    };

    daysList.forEach((dayLabel) => {
        const { fullDate } = getFormattedDate(dayLabel);

        // Record starting row index before adding rows
        const startRowIndex = sheet.rowCount + 1;

        // 2 Rows: Arrival and Departure
        ["Arrival", "Departure"].forEach((type) => {
            const row = sheet.addRow([]);

            // A = Type
            row.getCell("A").value = type;
            row.getCell("A").alignment = { vertical: "middle", horizontal: "left" };

            // B = Full Date (we will merge this cell after both rows are added)
            row.getCell("B").value = fullDate;
            row.getCell("B").alignment = { vertical: "middle", horizontal: "left" };

            // Use correct data source
            const source = type === "Arrival" ? data.flightsDocsInbound : data.flightsDocsOutbound;

            // C → onward (each hour column)
            hoursColumns.forEach((hour, hourIdx) => {
                const colIndex = 3 + hourIdx; // C = 3
                const cell = row.getCell(colIndex);

                const flightsMap = source?.[dayLabel]?.[hour] ?? null;

                if (flightsMap) {
                    const flightLines = Object.entries(flightsMap).map(([flight, count]) => `${flight} (${count})`);
                    cell.value = flightLines.join("\n"); // Multi-line content
                } else {
                    cell.value = "";
                }

                styleCell(cell);
                const color = getCellColor("flights", type);
                if (color) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        fgColor: { argb: color },
                    };
                }
            });
        });

        // Merge column B cells for the date (Arrival & Departure)
        const row1 = startRowIndex;
        const row2 = startRowIndex + 1;
        sheet.mergeCells(`B${row1}:B${row2}`);
    });

    // Set fixed column width for C to Z
    for (let i = 3; i <= 26; i++) {
        const column = sheet.getColumn(i);
        column.width = 20;
    }
};

export const makeExcelGenerator = async (data, base64Logo, startDate, endDate, setLoadingExcelFile) => {
    const Excel = await import("exceljs/dist/exceljs.min.js");
    const workSheetName = data.workSheetName;
    let workbook = new Excel.Workbook();
    const daysList = getDatesBetween(startDate, endDate);
    workbook.views = [
        {
            x: 0,
            y: 0,
            width: 10000,
            height: 20000,
            firstSheet: 0,
            activeTab: 0,
            visibility: "visible",
        },
    ];
    let sheets = [],
        sheet;
    let workSheetImageHeader = workbook.addImage({
        base64: base64Logo || Logos.MAIN_LOGO,
        extension: "png",
    });

    let headerLetter = "A";
    for (let cw_idx = 0; cw_idx < data.columnTitles.length - 1; cw_idx++) {
        headerLetter = nextLetter(headerLetter);
    }
    for (let idx = 0; idx < workSheetName.length; idx++) {
        sheets[idx] = workbook.addWorksheet(workSheetName[idx], {
            views: [
                { state: "frozen", xSplit: 0, ySplit: 2 },
                { rightToLeft: false },
            ],
        });
    }

    let dataAlignment = {
        vertical: "middle",
        horizontal: "center",
        wrapText: true,
    };
    let dataBorder = { bottom: { style: "thin" }, left: { style: "thin" } };
    let columnTitles = data.columnTitles;
    let columnWidth = data.columnWidth;


    sheets.map((x, i) => {
        let letter = "A";
        sheet = sheets[i];

        columnWidth.map((y, j) => {
            letter = nextLetter(letter);
        });
        sheet.views = [
            { state: 'frozen', ySplit: 2 }
        ];


        sheet.addImage(workSheetImageHeader, {
            ...getImageCells(columnTitles, 1),
            ext: { width: 500, height: 100 },
            editAs: "absolute",
        });

        sheet.mergeCells(`A1:${letter}1`);
        sheet.getRow(1).height = 90;


        sheet.addRow(columnTitles[0]);

        sheet.lastRow.alignment = dataAlignment;
        sheet.lastRow.border = dataBorder;
        sheet.lastRow.height = 25;

        sheet.eachRow(function (row, rowNumber) {
            row.eachCell((cell) => {
                if (rowNumber == 2) {
                    cell.fill = {
                        type: "pattern",
                        pattern: "solid",
                        size: "14",
                        fgColor: { argb: "e1faf5" },
                        color: { argb: "3b7ab4" },
                    };
                    sheet.getRow(2).font = {
                        size: 16,
                        family: 2,
                        color: { argb: "3b7ab4" },
                    };
                }
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
                sheet.getColumn(rowNumber).alignment = {
                    vertical: "middle",
                    horizontal: "center",
                };

                if (rowNumber != 2)
                    sheet.getColumn(rowNumber).font = {
                        size: 12,
                        family: 2,
                    };


            });
        });

        let rows = data.rows;
        const sheetName = workSheetName[i];
        const sheetData = rows[0][sheetName];
        if (!sheetData) {
            console.warn(`No data found for sheet "${sheetName}"`);
            return;
        }
        if (sheetName === "shortfall map") {

            renderShortfallMapSheet({
                sheet,
                data: sheetData,
                daysList,
            });
            return;
        }
        if (sheetName === "flights") {

            renderFlightsSheet({
                sheet,
                data: sheetData,
                daysList,
            });
            return;
        }

        const normalizedSources = {};
        sources.forEach((sourceKey) => {
            normalizedSources[sourceKey] = normalizeKeys(sheetData[sourceKey] || {});
        });

        daysList.forEach((dayLabel) => {
            const startRow = sheet.lastRow ? sheet.lastRow.number + 1 : 3;
            // Merge and write the date label into column B
            sheet.mergeCells(`B${startRow}:B${startRow + sources.length - 1}`);
            const dateCell = sheet.getCell(`B${startRow}`);
            dateCell.value = new Date(dayLabel).toLocaleDateString("en-GB", { day: 'numeric', month: 'short' });
            dateCell.alignment = { vertical: "middle", horizontal: "center" };

            // Fill 6 rows (one for each source)
            sources.forEach((sourceKey, idx) => {
                const rowIndex = startRow + idx;
                const row = sheet.getRow(rowIndex);
                row.getCell("A").value = firstColumnLabels[idx];
                row.getCell("A").alignment = { vertical: "middle", horizontal: "left" };

                const source = normalizedSources[sourceKey];
                const entry = source[dayLabel];

                const hourlyValues = hoursColumns.map((hour) => {
                    if (!entry) return 0;
                    if (sourceKey === "shortfall") {
                        return entry?.[hour] ?? 0;
                    } else {
                        return entry?.hours?.[hour] ?? 0;
                    }
                });
                // columns from c to last one
                hourlyValues.forEach((val, colIndex) => {
                    const col = String.fromCharCode("C".charCodeAt(0) + colIndex); // C to X
                    const cell = sheet.getCell(`${col}${rowIndex}`);
                    cell.value = val;

                    cell.alignment = { vertical: "middle", horizontal: "center" };
                    cell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };

                    const cellColor = getCellColor(sourceKey, val);
                    if (cellColor) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: cellColor },
                        };
                    }
                });

                const totalValue = sourceKey === "shortfall"
                    ? undefined
                    : entry?.total ?? undefined;

                if (totalValue !== undefined) {
                    const row = sheet.getRow(rowIndex); //ensures row exists
                    const totalCell = row.getCell("AA");  //now safe
                    totalCell.value = totalValue;
                    totalCell.alignment = { vertical: "middle", horizontal: "center" };
                    totalCell.font = { bold: true };
                    totalCell.border = {
                        top: { style: "thin" },
                        left: { style: "thin" },
                        bottom: { style: "thin" },
                        right: { style: "thin" },
                    };
                }

            });
        });
        sheet.getColumn("A").width = 15

    });

    let buf = await workbook.xlsx.writeBuffer();
    let xslFileName = data.fileName
        ? `${data.fileName}.xlsx`
        : `EXCEL_FILE_${new Date().getTime()}.xlsx`;

    const downloadUrl = window.URL.createObjectURL(new Blob([buf]));

    const link = document.createElement("a");

    link.href = downloadUrl;
    setLoadingExcelFile(false)

    link.setAttribute("download", xslFileName); //any other extension

    document.body.appendChild(link);

    link.click();
    return null;
};
